import { EVENT_CODE } from '~/constants/event'
import { isEmpty } from 'lodash-es'
import { useUserStore } from '~/store'
import { useUtilDetectAllStore, useCommonBannerStore } from '~/store/common'
import { CommonEventRequest } from '~/api/types/event'
import { commonEvent, getCouponInfo } from '~/api/event'
import { useGoogleTagManager } from '@/composables/utils/use-google-tag-manager'
import dayjs from 'dayjs'
import { useEventStore } from '~/store/store-event/event'

export const useCommonEvent = () => {
  const router = useRouter()
  const route = useRoute()
  const userStore = useUserStore()
  const { custInfo } = storeToRefs(userStore)
  const { isNativeApp, isDesktop } = useUtilDetectAllStore()
  const commonBannerStore = useCommonBannerStore()
  const isLogin = computed(() => !!custInfo.value?.loginId)
  const isProcess = ref(false)

  const { sendGAEvent } = useGoogleTagManager()
  const eventStore = useEventStore()

  async function callEvent(offerId: string, dialogRef: any) {
    if (isProcess.value) {
      return
    }

    if (isEmpty(offerId)) {
      return
    }

    if (!isLogin.value) {
      dialogRef?.open({
        message: $t('event.message.0002', '로그인 후 이용해 주세요.'),
        callbackFunction: goLogin
      })

      return
    }

    isProcess.value = true

    const payload: CommonEventRequest = {
      eventId: offerId,
      flag: EVENT_CODE.EVENT_SEPARATION.ENTRY,
      eventSpr: EVENT_CODE.EVENT_TYPE.COMMON,
      mediaCd: isDesktop ? EVENT_CODE.LDIN_MEDIA.INTERNET : EVENT_CODE.LDIN_MEDIA.MOBILE
    }
    const { data } = await commonEvent(payload)

    if (data.resultCode === '7614') {
      dialogRef?.open({
        message: $t(
          'event.message.0008',
          '앱에서만 참여할 수 있는 이벤트입니다. 오직 앱에서만 누릴 수 있는 다양한 혜택 지금 받아보세요!'
        ),
        isShowTwoButton: true,
        firstButtonLabel: '나중에 하기',
        secondButtonLabel: '앱으로 보기',
        callbackFunction: (isCancel: boolean) => {
          if (isCancel) {
            return
          } else {
            location.href = 'https://go.nsmall.com/home'
          }
        }
      })
    } else if (data.resultCode === '7699') {
      dialogRef?.open({
        message: $t(data.resultMessage),
        isShowTwoButton: true,
        firstButtonLabel: '확인',
        secondButtonLabel: '앱으로 보기',
        callbackFunction: (isCancel: boolean) => {
          if (isCancel) {
            return
          } else {
            if (isDesktop) {
              commonBannerStore.isShowAppDown = true
            } else {
              location.href = 'https://go.nsmall.com/home'
            }
          }
        }
      })
    } else {
      dialogRef?.open({
        message: $t(data.resultMessage)
      })
    }

    setSendGaEvent(offerId, data.resultCode)
    isProcess.value = false
  }

  async function callEventCheckPush(offerId: string, dialogRef: any) {
    if (isProcess.value) {
      return
    }

    if (isEmpty(offerId)) {
      return
    }

    if (isDesktop) {
      dialogRef?.open({
        message: $t('event.message.0001', '앱에서만 참여할 수 있는 이벤트입니다.')
      })
      return false
    } else if (!isNativeApp) {
      dialogRef?.open({
        message: $t(
          'event.message.0008',
          '앱에서만 참여할 수 있는 이벤트입니다. 오직 앱에서만 누릴 수 있는 다양한 혜택 지금 받아보세요!'
        ),
        isShowTwoButton: true,
        firstButtonLabel: '나중에 하기',
        secondButtonLabel: '앱으로 보기',
        callbackFunction: (isCancel: boolean) => {
          if (isCancel) {
            return
          } else {
            location.href = 'https://go.nsmall.com/home'
          }
        }
      })
      return
    }

    if (!isLogin.value) {
      dialogRef?.open({
        message: $t('event.message.0002', '로그인 후 이용해 주세요.'),
        callbackFunction: goLogin
      })
      return
    }

    dialogRef?.open({
      message: $t('event.message.0006', '쇼핑알림(PUSH) 받기에 동의한 고객만 참여가 가능합니다.'),
      callbackFunction: goMypageSetting
    })

    isProcess.value = true

    const payload: CommonEventRequest = {
      eventId: offerId,
      flag: EVENT_CODE.EVENT_SEPARATION.ENTRY,
      eventSpr: EVENT_CODE.EVENT_TYPE.COMMON,
      mediaCd: isDesktop ? EVENT_CODE.LDIN_MEDIA.INTERNET : EVENT_CODE.LDIN_MEDIA.MOBILE
    }
    const { data } = await commonEvent(payload)

    dialogRef?.open({
      message: $t(data.resultMessage)
    })

    setSendGaEvent(offerId, data.resultCode)
    isProcess.value = false
  }

  function goMypageSetting(): void {
    router.push({ path: '/my-page/setting' })
  }

  function goLogin(): void {
    router.push({ path: '/customer/login', query: { joinRedirectUri: route.path } })
  }

  async function setSendGaEvent(offerId: string, resultCode: string) {
    if (eventStore.winCodeList.includes(resultCode)) {
      const resCoupon = await getCouponInfo({ offerIdfr: offerId })
      const couponDetail = resCoupon.data.resultData?.couponDetail

      const routeTitle = String(typeof route.meta.title === 'function' ? route.meta.title() : route.meta.title)
      const eventPage = isDesktop ? `PC>${routeTitle}` : isNativeApp ? `App>${routeTitle}` : `Mobile>${routeTitle}`

      sendGAEvent({
        event_name: 'application_offer',
        ep_event_page: eventPage,
        offer_id: offerId,
        user_id: custInfo.value.custNum,
        date: dayjs().format('YYYY-MM-DD'),
        offer_coupon_id: isEmpty(couponDetail?.cpIdfr) ? '' : couponDetail?.cpIdfr,
        offer_coupon_name: isEmpty(couponDetail?.cpNm) ? '' : couponDetail?.cpNm,
        offer_coupon_expiration_date: isEmpty(couponDetail?.cpValidEnddttm)
          ? ''
          : dayjs(couponDetail?.cpValidEnddttm).format('YYYY-MM-DD'),
        page_id: route.path
      })
    }
  }

  return {
    callEvent,
    callEventCheckPush
  }

  /*
  // 이벤트 호출 예시
  import { useCommonEvent } from '~/composable/utils/index'
  import Dialog from '@/components/dialog/dialog-modal/dialog.vue'

  const { callEvent } = useCommonEvent()
  const dialogRef = ref<InstanceType<typeof Dialog>>()

  // 이벤트 호출 함수
  function eventTest(): void {
    callEvent(offerId, dialogRef.value)
    callEventCheckPush(offerId, dialogRef.value)
  }

  <button type="button" @click="eventTest('aaaa', dialogRef.value)">이벤트 호출 버튼</button>
   */
}
